import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useDocumentGeneration } from '@/hooks/useDocumentGeneration';
import { FileDown, Printer, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface ReportPDFGeneratorProps {
  state: string;
  year: number;
  quarter?: number;
  reportData: any;
  onReportGenerated?: (result: { fileUrl: string, documentId: string }) => void;
}

export function ReportPDFGenerator({ 
  state, 
  year, 
  quarter, 
  reportData,
  onReportGenerated 
}: ReportPDFGeneratorProps) {
  const { generateDocument, logDocumentAccess, isLoading } = useDocumentGeneration();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [templateType, setTemplateType] = useState<string>('regulatory');

  const handleGenerateReport = async () => {
    const reportPeriod = quarter ? `Q${quarter} ${year}` : `${year}`;
    const fileName = `${state.toUpperCase()}-${reportPeriod.replace(/\s/g, '-')}-Report.pdf`;
    
    generateDocument({
      template: templateType,
      documentType: 'report',
      data: {
        state,
        year,
        quarter,
        reportPeriod,
        reportData,
        generatedAt: new Date().toISOString()
      },
      fileName
    }, (result) => {
      if (result.success && result.fileUrl && result.documentId) {
        setPreviewUrl(result.fileUrl);
        setDocumentId(result.documentId);
        
        if (onReportGenerated) {
          onReportGenerated({
            fileUrl: result.fileUrl,
            documentId: result.documentId
          });
        }
      }
    });
  };

  const handlePreview = () => {
    if (documentId) {
      logDocumentAccess(documentId, 'view');
    }
    setPreviewOpen(true);
  };

  const handlePrint = () => {
    if (previewUrl && documentId) {
      logDocumentAccess(documentId, 'print');
      const printWindow = window.open(previewUrl, '_blank');
      printWindow?.addEventListener('load', () => {
        printWindow.print();
      });
    }
  };

  const handleDownload = () => {
    if (previewUrl && documentId) {
      logDocumentAccess(documentId, 'download');
      const a = document.createElement('a');
      a.href = previewUrl;
      a.download = `${state.toUpperCase()}-${quarter ? `Q${quarter}-` : ''}${year}-Report.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Generate Report PDF</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="template-type">Report Template</Label>
          <Select
            value={templateType}
            onValueChange={setTemplateType}
          >
            <SelectTrigger id="template-type" className="w-full">
              <SelectValue placeholder="Select template" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="regulatory">Regulatory Report</SelectItem>
              <SelectItem value="summary">Summary Report</SelectItem>
              <SelectItem value="detailed">Detailed Report</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {!previewUrl ? (
          <Button 
            onClick={handleGenerateReport} 
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2"
          >
            <FileDown className="h-4 w-4" />
            {isLoading ? 'Generating...' : 'Generate Report PDF'}
          </Button>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handlePreview} className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Preview
            </Button>
            <Button variant="outline" onClick={handlePrint} className="flex items-center gap-2">
              <Printer className="h-4 w-4" />
              Print
            </Button>
            <Button variant="outline" onClick={handleDownload} className="flex items-center gap-2">
              <FileDown className="h-4 w-4" />
              Download
            </Button>
          </div>
        )}
      </CardContent>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Report Preview</DialogTitle>
          </DialogHeader>
          {previewUrl && (
            <div className="w-full h-[70vh] overflow-auto">
              <iframe 
                src={previewUrl} 
                title="Report Preview" 
                className="w-full h-full border-0" 
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
